import {
  ANGLE_GUIDE, type CreatePhotoSession, PHOTO_ANGLES, PHOTO_SESSION_KINDS, type Photo, type PhotoAngle, type PhotoSession,
  type PhotoSessionKind, SESSION_PRESETS,
} from '@dnx/contracts';
import { Alert, AngleGuide, Badge, Button, Card, Dialog, Field, Select, Spinner, Switch, cn, toJpeg } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Camera, Columns2, ImageUp, Send, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { api, uploadImage } from '../lib/api.js';
import { meQuery } from '../lib/session.js';
import { when } from './queries.js';

const sessionsQuery = (patientId: string) => queryOptions({
  queryKey: ['patients', patientId, 'photo-sessions'], queryFn: () => api<PhotoSession[]>(`/patients/${patientId}/photo-sessions`),
});
const KIND_LABELS: Record<PhotoSessionKind, string> = {
  evaluation: 'Avaliação', pre_op: 'Pré-operatório', post_op: 'Pós-operatório', follow_up: 'Acompanhamento', other: 'Outro',
};
const STATUS: Record<PhotoSession['status'], [string, 'primary' | 'success' | 'neutral']> = {
  open: ['Aguardando fotos', 'primary'], complete: ['Completo', 'success'], cancelled: ['Cancelado', 'neutral'],
};

function RequestDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<PhotoSessionKind>('evaluation');
  const [angles, setAngles] = useState<PhotoAngle[]>(SESSION_PRESETS.evaluation.angles);
  const [notify, setNotify] = useState(true);
  const create = useMutation({
    mutationFn: () => api<{ session: PhotoSession; notified: boolean; portalReady: boolean }>(`/patients/${patientId}/photo-sessions`, {
      method: 'POST', body: { kind, angles, notify } satisfies CreatePhotoSession,
    }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['patients', patientId, 'photo-sessions'] }),
  });
  const res = create.data;
  return (
    <Dialog open onClose={onClose} title="Pedir fotos ao paciente" description="O paciente tira pelo portal, com o passo a passo e o molde de cada ângulo.">
      {res ? (
        <div className="grid gap-4 p-5">
          <Alert tone={res.notified ? 'success' : 'info'} title={res.notified ? 'Pedido enviado pelo WhatsApp' : 'Pedido criado'}>
            {res.notified ? 'O paciente recebeu o link do portal.' : 'Sem aviso no WhatsApp. Você também pode enviar as fotos daqui, durante a consulta.'}
          </Alert>
          {!res.portalReady && <Alert tone="warning" title="O paciente ainda não ativou o portal">Envie o convite de acesso (no lead ou na conversa, em “Área do paciente”) para ele conseguir enviar as fotos.</Alert>}
          <div className="flex justify-end"><Button onClick={onClose}>Fechar</Button></div>
        </div>
      ) : (
        <div className="grid gap-4 p-5">
          <Field label="Momento">
            <Select value={kind} onChange={(e) => { const k = e.target.value as PhotoSessionKind; setKind(k); setAngles(SESSION_PRESETS[k].angles); }}>
              {PHOTO_SESSION_KINDS.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
            </Select>
          </Field>
          <div>
            <p className="text-[13px] font-medium text-fg">Ângulos</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {PHOTO_ANGLES.map((a) => {
                const on = angles.includes(a);
                return (
                  <button key={a} type="button" aria-pressed={on} onClick={() => setAngles(on ? angles.filter((x) => x !== a) : PHOTO_ANGLES.filter((x) => x === a || angles.includes(x)))}
                    className={cn('h-8 rounded-full border px-3 text-[13px]', on ? 'border-primary bg-primary-soft text-primary' : 'border-border-strong text-fg-muted hover:text-fg')}>
                    {ANGLE_GUIDE[a].label}
                  </button>
                );
              })}
            </div>
          </div>
          <Switch checked={notify} onChange={setNotify} label="Avisar pelo WhatsApp" description="Manda o link do portal com o passo a passo." />
          {create.error && <Alert tone="danger">{create.error.message}</Alert>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button disabled={!angles.length} loading={create.isPending} onClick={() => create.mutate()}><Send className="size-4" />Pedir fotos</Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function Tile({ session, angle, photo, canUpload, onOpen }: { session: PhotoSession; angle: PhotoAngle; photo?: Photo; canUpload: boolean; onOpen: (p: Photo) => void }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const up = useMutation({
    mutationFn: async (f: File) => { const j = await toJpeg(f); return uploadImage<Photo>(`/photo-sessions/${session.id}/photos`, j.blob, j, { angle }); },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['patients'] }),
  });
  return (
    <div className="grid gap-1.5">
      <div className="group relative aspect-[3/4] overflow-hidden rounded-lg border border-border bg-surface-muted">
        {photo ? (
          <button type="button" onClick={() => onOpen(photo)} className="size-full" aria-label={`Ver ${ANGLE_GUIDE[angle].label}`}>
            <img src={photo.url} alt={ANGLE_GUIDE[angle].label} loading="lazy" className="size-full object-cover" />
          </button>
        ) : <AngleGuide angle={angle} className="absolute inset-[10%] h-[80%] w-[80%] text-fg-subtle/50" />}
        {up.isPending && <div className="absolute inset-0 grid place-items-center bg-surface/70"><Spinner className="size-5 text-fg-subtle" label="Enviando" /></div>}
        {canUpload && session.status !== 'cancelled' && (
          <button type="button" onClick={() => input.current?.click()} aria-label={`Enviar foto: ${ANGLE_GUIDE[angle].label}`}
            className={cn('absolute right-1.5 bottom-1.5 grid size-8 place-items-center rounded-full bg-surface/90 text-fg shadow-card', photo && 'opacity-0 group-hover:opacity-100 focus:opacity-100')}>
            <ImageUp className="size-4" />
          </button>
        )}
        <input ref={input} type="file" accept="image/*" className="sr-only" tabIndex={-1} onChange={(e) => { const f = e.target.files?.[0]; if (f) up.mutate(f); e.target.value = ''; }} />
      </div>
      <p className="truncate text-[12px] text-fg-muted">{ANGLE_GUIDE[angle].label}{photo?.uploadedBy === 'patient' && <span className="text-fg-subtle"> · paciente</span>}</p>
      {up.error && <p className="text-[12px] text-danger">{up.error.message}</p>}
    </div>
  );
}

function Compare({ sessions, onClose }: { sessions: PhotoSession[]; onClose: () => void }) {
  const withPhotos = sessions.filter((s) => s.photos.length);
  const [a, setA] = useState(withPhotos.at(-1)?.id ?? '');
  const [b, setB] = useState(withPhotos[0]?.id ?? '');
  const sa = withPhotos.find((s) => s.id === a);
  const sb = withPhotos.find((s) => s.id === b);
  const common = PHOTO_ANGLES.filter((x) => sa?.photos.some((p) => p.angle === x) && sb?.photos.some((p) => p.angle === x));
  const [angle, setAngle] = useState<PhotoAngle | ''>('');
  const cur = (angle && common.includes(angle) ? angle : common[0]) as PhotoAngle | undefined;
  const [split, setSplit] = useState(50);
  const pa = sa?.photos.find((p) => p.angle === cur);
  const pb = sb?.photos.find((p) => p.angle === cur);
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Antes"><Select value={a} onChange={(e) => setA(e.target.value)}>{withPhotos.map((s) => <option key={s.id} value={s.id}>{s.title} · {when(s.createdAt).split(',')[0]}</option>)}</Select></Field>
        <Field label="Depois"><Select value={b} onChange={(e) => setB(e.target.value)}>{withPhotos.map((s) => <option key={s.id} value={s.id}>{s.title} · {when(s.createdAt).split(',')[0]}</option>)}</Select></Field>
        <Field label="Ângulo"><Select value={cur ?? ''} onChange={(e) => setAngle(e.target.value as PhotoAngle)}>{common.map((x) => <option key={x} value={x}>{ANGLE_GUIDE[x].label}</option>)}</Select></Field>
        <Button variant="ghost" className="ml-auto" onClick={onClose}><X className="size-4" />Fechar</Button>
      </div>
      {pa && pb ? (
        <div className="mt-5">
          <div className="relative mx-auto aspect-[3/4] w-full max-w-[calc(62vh*0.75)] overflow-hidden rounded-xl bg-surface-muted select-none">
            <img src={pb.url} alt="Depois" className="absolute inset-0 size-full object-cover" />
            <img src={pa.url} alt="Antes" className="absolute inset-0 size-full object-cover" style={{ clipPath: `inset(0 ${100 - split}% 0 0)` }} />
            <span aria-hidden className="absolute inset-y-0 w-0.5 bg-white shadow" style={{ left: `${split}%` }} />
            <span className="absolute top-3 left-3 rounded bg-black/55 px-2 py-0.5 text-[12px] text-white">Antes</span>
            <span className="absolute top-3 right-3 rounded bg-black/55 px-2 py-0.5 text-[12px] text-white">Depois</span>
          </div>
          <input type="range" min={0} max={100} value={split} onChange={(e) => setSplit(Number(e.target.value))} aria-label="Divisão antes e depois" className="mt-4 w-full accent-[var(--dnx-primary)]" />
        </div>
      ) : <p className="mt-5 text-sm text-fg-muted">Escolha duas sessões com o mesmo ângulo fotografado.</p>}
    </Card>
  );
}

/** Fotos do paciente: pedidos (sessões), envio pela equipe e comparação antes/depois. */
export function PhotosTab({ patientId }: { patientId: string }) {
  const { data: me } = useSuspenseQuery(meQuery);
  const canWrite = me.permissions.includes('clinical.record.write');
  const qc = useQueryClient();
  const { data, isLoading } = useQuery(sessionsQuery(patientId));
  const [requesting, setRequesting] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [viewing, setViewing] = useState<Photo | null>(null);
  const cancel = useMutation({
    mutationFn: (id: string) => api<void>(`/photo-sessions/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['patients', patientId, 'photo-sessions'] }),
  });
  const withPhotos = (data ?? []).filter((s) => s.photos.length).length;
  if (isLoading) return <Spinner className="size-5 text-fg-subtle" label="Carregando" />;
  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center gap-2">
        {canWrite && <Button onClick={() => setRequesting(true)}><Camera className="size-4" />Pedir fotos</Button>}
        {withPhotos >= 2 && !comparing && <Button variant="secondary" onClick={() => setComparing(true)}><Columns2 className="size-4" />Comparar antes e depois</Button>}
        <p className="ml-auto text-[12.5px] text-fg-muted">Fotos cifradas, sem localização do aparelho. Fazem parte do prontuário e não são apagadas.</p>
      </div>
      {comparing && data && <Compare sessions={data} onClose={() => setComparing(false)} />}
      {data?.map((s) => (
        <Card key={s.id} className="p-5">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-fg">{s.title}</h3>
            <Badge tone={STATUS[s.status][1]}>{STATUS[s.status][0]}</Badge>
            <span className="text-[12.5px] text-fg-muted">{when(s.createdAt)}{s.notifiedAt ? ' · avisado no WhatsApp' : ''}</span>
            {canWrite && s.status === 'open' && <Button size="sm" variant="ghost" className="ml-auto" loading={cancel.isPending && cancel.variables === s.id} onClick={() => cancel.mutate(s.id)}>Cancelar pedido</Button>}
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            {s.angles.map((a) => <Tile key={a} session={s} angle={a} photo={s.photos.find((p) => p.angle === a)} canUpload={canWrite} onOpen={setViewing} />)}
          </div>
        </Card>
      ))}
      {!data?.length && (
        <Card className="grid place-items-center gap-3 p-10 text-center">
          <Camera className="size-6 text-fg-subtle" />
          <p className="font-semibold text-fg">Nenhuma foto ainda</p>
          <p className="max-w-md text-sm text-fg-muted">Peça as fotos da avaliação: o paciente recebe o link no WhatsApp e fotografa cada ângulo com o molde na tela. Na consulta, a equipe também pode enviar direto daqui.</p>
        </Card>
      )}
      {requesting && <RequestDialog patientId={patientId} onClose={() => setRequesting(false)} />}
      <Dialog open={!!viewing} onClose={() => setViewing(null)} title={viewing ? ANGLE_GUIDE[viewing.angle].label : ''}
        description={viewing ? `${viewing.uploadedBy === 'patient' ? 'Enviada pelo paciente' : `Enviada por ${viewing.uploadedByName ?? 'equipe'}`} · ${when(viewing.createdAt)}` : undefined}
        className="w-[min(92vw,900px)]">
        {viewing && <img src={viewing.url} alt={ANGLE_GUIDE[viewing.angle].label} className="max-h-[78vh] w-full object-contain" />}
      </Dialog>
    </div>
  );
}
